import { Component, computed, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { IChatSettings } from "@models/chat.model";
import { ChatService } from "@services/chat/chat.service";
import { chatContactInfoMessage, findChatContactInfo } from "@utils/contact-info.util";

export const MAX_QUICK_REPLIES = 20;
export const MAX_QUICK_REPLY_LENGTH = 300;
export const MAX_AWAY_LENGTH = 500;

/** Message settings: saved quick replies and the away message (automatic reply). */
@Component({
  selector: "fh-message-settings-dialog",
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
  ],
  template: `
    <h2 mat-dialog-title>Message settings</h2>
    <mat-dialog-content>
      <h3 class="section">Quick replies</h3>
      <p class="hint">
        Saved answers you can insert with one tap while replying (up to {{ maxReplies }}).
      </p>

      @if (quickReplies().length) {
        <ul class="replies">
          @for (reply of quickReplies(); track $index) {
            <li>
              <span class="reply-text">{{ reply }}</span>
              <button
                mat-icon-button
                type="button"
                aria-label="Remove quick reply"
                (click)="removeReply($index)"
              >
                <mat-icon>delete_outline</mat-icon>
              </button>
            </li>
          }
        </ul>
      }

      @if (quickReplies().length < maxReplies) {
        <div class="add-row">
          <mat-form-field appearance="outline" class="grow">
            <mat-label>New quick reply</mat-label>
            <textarea
              matInput
              rows="2"
              [maxlength]="maxReplyLength"
              [ngModel]="newReply()"
              (ngModelChange)="newReply.set($event)"
              placeholder="e.g. Yes, this is in stock and ships within 2 days."
            ></textarea>
            <mat-hint align="end">{{ newReply().length }} / {{ maxReplyLength }}</mat-hint>
          </mat-form-field>
          <button
            mat-stroked-button
            type="button"
            [disabled]="!newReply().trim()"
            (click)="addReply()"
          >
            <mat-icon>add</mat-icon>
            Add
          </button>
        </div>
      }

      <h3 class="section">Away message</h3>
      <mat-slide-toggle
        [ngModel]="awayEnabled()"
        (ngModelChange)="awayEnabled.set($event)"
        color="primary"
      >
        Send an automatic reply when shoppers message you
      </mat-slide-toggle>
      <p class="hint">Sent at most once every 12 hours in each conversation.</p>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Away message</mat-label>
        <textarea
          matInput
          rows="3"
          [maxlength]="maxAwayLength"
          [ngModel]="awayMessage()"
          (ngModelChange)="awayMessage.set($event)"
          placeholder="Thanks for your message! We're away and will reply as soon as we can."
        ></textarea>
        <mat-hint align="end">{{ awayMessage().length }} / {{ maxAwayLength }}</mat-hint>
      </mat-form-field>

      @if (contactWarning()) {
        <p class="warning">{{ contactWarning() }}</p>
      }
      @if (error()) {
        <p class="warning">{{ error() }}</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button
        mat-flat-button
        color="primary"
        [disabled]="saving() || !!contactWarning() || (awayEnabled() && !awayMessage().trim())"
        (click)="save()"
      >
        {{ saving() ? "Saving..." : "Save" }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      /* 16px text in the fields: iOS zooms in on anything smaller when tapped. */
      .mat-mdc-input-element {
        font-size: 16px;
      }

      .section {
        margin: 8px 0 4px;
        font-size: 16px;
        font-weight: 500;
      }
      .hint {
        margin: 0 0 8px;
        font-size: 13px;
        color: rgba(0, 0, 0, 0.6);
      }
      .replies {
        list-style: none;
        padding: 0;
        margin: 0 0 8px;
      }
      .replies li {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 0 4px 12px;
        border-radius: 8px;
        background-color: #f5f5f5;
        margin-bottom: 6px;
      }
      .reply-text {
        flex: 1;
        min-width: 0;
        white-space: pre-wrap;
        word-break: break-word;
        font-size: 14px;
      }
      .add-row {
        display: flex;
        align-items: flex-start;
        gap: 8px;
      }
      .add-row button {
        margin-top: 8px;
      }
      .grow {
        flex: 1;
      }
      .full {
        width: 100%;
        margin-top: 8px;
      }
      .warning {
        margin: 8px 0 0;
        padding: 8px 12px;
        border-radius: 6px;
        background-color: #fdecea;
        color: #b71c1c;
        font-size: 13px;
      }
    `,
  ],
})
export class MessageSettingsDialogComponent {
  private chatService = inject(ChatService);
  private dialogRef = inject(MatDialogRef<MessageSettingsDialogComponent, IChatSettings>);
  private data = inject<IChatSettings>(MAT_DIALOG_DATA);

  readonly maxReplies = MAX_QUICK_REPLIES;
  readonly maxReplyLength = MAX_QUICK_REPLY_LENGTH;
  readonly maxAwayLength = MAX_AWAY_LENGTH;

  quickReplies = signal<string[]>([...(this.data?.quickReplies ?? [])]);
  newReply = signal<string>("");
  awayEnabled = signal<boolean>(!!this.data?.awayEnabled);
  awayMessage = signal<string>(this.data?.awayMessage ?? "");
  saving = signal<boolean>(false);
  error = signal<string | null>(null);

  /** Saved replies and the away message are sent into conversations, so the same rule applies. */
  contactWarning = computed(() => {
    const found = findChatContactInfo(
      [...this.quickReplies(), this.newReply(), this.awayMessage()].join("\n")
    );
    return found.length ? chatContactInfoMessage(found) : null;
  });

  addReply(): void {
    const reply = this.newReply().trim().slice(0, MAX_QUICK_REPLY_LENGTH);
    if (!reply || this.quickReplies().length >= MAX_QUICK_REPLIES) return;
    this.quickReplies.update((list) => [...list, reply]);
    this.newReply.set("");
  }

  removeReply(index: number): void {
    this.quickReplies.update((list) => list.filter((_, i) => i !== index));
  }

  save(): void {
    // A reply typed but not yet added is kept too.
    if (this.newReply().trim()) this.addReply();
    const settings: IChatSettings = {
      quickReplies: this.quickReplies().slice(0, MAX_QUICK_REPLIES),
      awayEnabled: this.awayEnabled(),
      awayMessage: this.awayMessage().trim().slice(0, MAX_AWAY_LENGTH),
    };
    this.saving.set(true);
    this.error.set(null);
    this.chatService.saveSettings(settings).subscribe({
      next: (res) => {
        this.saving.set(false);
        if (res.status) this.dialogRef.close(res.data ?? settings);
        else this.error.set(res.message || "Could not save settings.");
      },
      error: () => {
        this.saving.set(false);
        this.error.set("Could not save settings.");
      },
    });
  }
}
